import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { ArrowUp, Paperclip } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { MentionPopover, type MentionPopoverHandle } from "./MentionPopover";
import {
  useChatContext,
  type EntityContext,
} from "@/stores/chat-context-store";
import { ChatContextChips } from "./ChatContextChips";

interface GlobalChatInputProps {
  placeholder?: string;
  onSend: (message: string) => void;
}

export function GlobalChatInput({ placeholder, onSend }: GlobalChatInputProps) {
  const { t } = useTranslation(["common"]);
  const resolvedPlaceholder = placeholder ?? t("ui.chatPanel.askPlaceholder");
  const [value, setValue] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mentionRef = useRef<MentionPopoverHandle>(null);
  const addEntity = useChatContext((s) => s.addEntity);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-resize textarea on value change
  const resizeTextarea = () => {
    const el = inputRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  };

  const trimmed = value.trim();

  function handleSend() {
    if (!trimmed || mentionOpen) return;
    onSend(trimmed);
    setValue("");
    requestAnimationFrame(resizeTextarea);
  }

  function handleChange(newValue: string) {
    setValue(newValue);
    requestAnimationFrame(resizeTextarea);

    const cursorPos = inputRef.current?.selectionStart ?? newValue.length;
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex >= 0) {
      const charBefore = lastAtIndex > 0 ? newValue[lastAtIndex - 1] : " ";
      if (charBefore === " " || lastAtIndex === 0) {
        const query = textBeforeCursor.slice(lastAtIndex + 1);
        if (!query.includes(" ")) {
          setMentionOpen(true);
          setMentionQuery(query);
          setMentionStartIndex(lastAtIndex);
          return;
        }
      }
    }

    if (mentionOpen) {
      setMentionOpen(false);
      setMentionQuery("");
      setMentionStartIndex(-1);
    }
  }

  const handleMentionSelect = useCallback(
    (entity: EntityContext) => {
      addEntity(entity);

      if (mentionStartIndex >= 0) {
        const before = value.slice(0, mentionStartIndex);
        const cursorPos = inputRef.current?.selectionStart ?? value.length;
        const after = value.slice(cursorPos);
        setValue(before + after);
      }

      setMentionOpen(false);
      setMentionQuery("");
      setMentionStartIndex(-1);
      inputRef.current?.focus();
    },
    [addEntity, mentionStartIndex, value],
  );

  const handleMentionClose = useCallback(() => {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStartIndex(-1);
    inputRef.current?.focus();
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionOpen && mentionRef.current) {
      const handled = mentionRef.current.handleKey(e.key);
      if (handled) {
        e.preventDefault();
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const hasContent = trimmed.length > 0;

  return (
    <div className="px-4 pb-4 pt-2">
      {/* Card container wrapping chips + input */}
      <div className="rounded-2xl bg-page border border-border/40 shadow-sm">
        {/* Context chips inside the card */}
        <ChatContextChips />

        {/* Input row */}
        <div className="relative px-3.5 pb-3 pt-2">
          <MentionPopover
            ref={mentionRef}
            open={mentionOpen}
            onSelect={handleMentionSelect}
            onClose={handleMentionClose}
            searchQuery={mentionQuery}
          />

          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={resolvedPlaceholder}
              rows={1}
              className="flex-1 bg-transparent text-[13px] text-primary placeholder:text-muted/60 outline-none resize-none leading-relaxed min-h-[24px] max-h-[120px] py-1"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={mentionOpen}
            />

            {/* Action icons */}
            <div className="flex items-center gap-1 shrink-0 pb-0.5">
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-muted/50 hover:text-muted hover:bg-black/5 transition-colors duration-150 cursor-pointer"
                aria-label={t("ui.chatPanel.attach")}
                onClick={() => {
                  /* placeholder for @ mention trigger */
                  const current = value;
                  setValue(`${current}@`);
                  inputRef.current?.focus();
                  handleChange(`${current}@`);
                }}
              >
                <Paperclip size={15} strokeWidth={2} />
              </button>

              <button
                type="button"
                onClick={handleSend}
                disabled={!hasContent}
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
                  hasContent
                    ? "bg-accent text-white shadow-sm hover:bg-accent/90 hover:scale-105"
                    : "bg-black/8 text-muted/30"
                }`}
                aria-label={t("ui.chatPanel.sendMessage")}
              >
                <ArrowUp size={15} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
